import {apolloClient} from '../apolloClient';
import {
  EntityFeatureFlagsDocument,
  type EntityFeatureFlagsQuery,
  type EntityFeatureFlagsQueryVariables,
  OrgType,
  RampIdType,
  useEntityFeatureFlagsQuery,
  useOrganizationFeatureFlagsQuery,
  useViewerFeatureFlagsQuery,
  useViewerOrganizationFeatureFlagsQuery,
  ViewerFeatureFlagsDocument,
  type ViewerFeatureFlagsQuery,
  type ViewerFeatureFlagsQueryVariables,
} from '../generated/graphql';
import {type ApolloClient} from '../state/types';
import {getDatadogRum} from './datadog/datadogRum';
import {useDatadogFeatureFlagEvaluation} from './datadog/datadogRumFlagEval';
import {propagateErrorsContext} from './errors';
import {type FeatureFlagList} from './rampFeatureFlags/stores/flagMap';
import {isIntegrationBypassActive} from './rampFeatureFlags/stores/sharedEvaluators';

/**
 * MIGRATION: under a route that mounts `useInitializeRampFlagsStores`, prefer
 * the store-backed readers in `./rampFeatureFlags/stores/*` — they share one
 * cache per page instead of one Apollo subscription per call. These Apollo
 * primitives stay for non-React call sites (`checkEntityRampFlag`,
 * `checkViewerRampFlag`), callers that need an explicit non-route entity,
 * and routes that haven't been onboarded. Evaluation logic is shared via
 * `sharedEvaluators.ts` so both layers stay in lockstep.
 */

export type RampFlagStatus = {
  isEnabled: boolean;
  loading: boolean;
};

// TODO: we should auto generate this list from ramp_settings.go's RampFeatures
// list. For now, we have to manually keep this list in sync.
// ALSO TODO: we should also have the server tell us the ramp key *type* so that the
// user just calls useRampFlag('disable-fuzzy-search') and we can figure out the type
// based on the RampSettings' IDType in ramp_settings.go
export type RampKey =
  | 'account-selector'
  | 'automation_entity_scope'
  | 'automation_hub'
  | 'automation_execution_history'
  | 'backend_line_construction_entity'
  | 'backend_line_construction_grouped_entity'
  | 'backend_line_construction_org'
  | 'backend_line_construction_reports_entity'
  | 'backend_line_construction_reports_org'
  | 'backend_line_construction_smoothing'
  | 'backend_line_construction_time_units'
  | 'backend_line_construction_user'
  | 'backend_smoothing_deployment'
  | 'backend_smoothing_entity'
  | 'brsdq_without_run_info_entity'
  | 'brsdq_without_run_info_org'
  | 'brsdq_without_run_info_user'
  | 'bulk_org_invitations'
  | 'collection_overview_phase_1'
  | 'combined_metrics_runs_limit_ui'
  | 'coreweave_evals_public_queue'
  | 'coreweave_observability'
  | 'create-org-scoped-keys'
  | 'disable-fuzzy-search'
  | 'dynamic_full_screen_bin_sizing_org'
  | 'embedded-panels-entity'
  | 'embedded-panels-user'
  | 'enable-opfs-cache'
  | 'enable_user_secrets'
  | 'entity_less_profile_page'
  | 'fast_workspace_autosave'
  | 'force-full-fidelity-in-reports-entity'
  | 'force-full-fidelity-in-reports-user'
  | 'format-x-axis-entity'
  | 'format-x-axis-user'
  | 'full_fidelity_ranges_deployment'
  | 'full_fidelity_ranges_entity'
  | 'full_fidelity_ranges_user'
  | 'full_fidelity_run_per_chart_org'
  | 'full_fidelity_run_per_chart_user'
  | 'fullscreen_line_plot_editing_entity'
  | 'fullscreen_line_plot_editing_user'
  | 'global-registry'
  | 'gql-long-term-cache-entity'
  | 'gql-long-term-cache-user'
  | 'hide_stop_run_ui'
  | 'hide_weave_monitors_tab'
  | 'hide-summary-histograms'
  | 'immer-auto-freeze-enabled'
  | 'increase_full_screen_max_runs_org'
  | 'increase_full_screen_max_runs_user'
  | 'inference_as_product'
  | 'inference-catalog'
  | 'inference_csv_cached_uncached_columns'
  | 'issues-observability-entity'
  | 'issues-observability-org'
  | 'issues-observability-user'
  | 'line-construction-profiling'
  | 'link_weave_prompts_to_registry'
  | 'lineage_modernization_phase_2'
  | 'marimo_notebook_management'
  | 'marimo_notebook_runtime'
  | 'marimo_panels_user'
  | 'media_browser_modal_enabled_entity'
  | 'media_browser_modal_enabled_user'
  | 'media_files_query_with_urls'
  | 'media_compare_mode_discovery'
  | 'media_panel_compare_mode_entity'
  | 'media_panel_compare_mode_org'
  | 'media_panel_compare_mode_user'
  | 'media_panel_layout_refactor'
  | 'media_panel_tracking_debug_entity'
  | 'media_panel_tracking_debug_user'
  | 'media-panel-tracking-entity'
  | 'media-panel-tracking-org'
  | 'media-panel-tracking-user'
  | 'member-table-last-active-at'
  | 'mobile_api_key_login_deployment'
  | 'model-registry-migration-notification'
  | 'multi-runset-history-keys-entity'
  | 'multi-runset-history-keys-org'
  | 'multi-runset-history-keys-user'
  | 'ngw_truncated_panel_gen'
  | 'ngw_add_panels_drawer'
  | 'ngw_line_plot_picker'
  | 'no-keysets-org'
  | 'no-keysets-user'
  | 'org_alerts_tab'
  | 'org-level-service-accounts'
  | 'enable_parallel_gzip_delta_queries_org'
  | 'pinned_runs'
  | 'pro_plan_monthly_option'
  | 'react_concurrent_mode'
  | 'react_concurrent_mode_rollback'
  | 'reference_bucket_byob'
  | 'registry_homepage_adjustments'
  | 'registry_ui_phase_2'
  | 'registry_ui_phase_3'
  | 'registry-automations'
  | 'registry-observer-role'
  | 'registry-search'
  | 'registry-team-permissions'
  | 'run_logs_pagination'
  | 'run_selector_pin-on-hover'
  | 'run-metric-notifications-entity'
  | 'run-metric-notifications'
  | 'saas_coreweave_storage_byob'
  | 'sandboxes'
  | 'sampled-runs-line-plot-notifications'
  | 'scatter_plot_semantic_colors'
  | 'self_service_cancellation'
  | 'semantic_legend_bucket_editing_org'
  | 'shortcut_drawer'
  | 'show_org_activity_page'
  | 'smart_feature_settings'
  | 'sparse_compression_user'
  | 'sparse_panel_overrides_user'
  | 'source_artifact_protection'
  | 'starred_registries'
  | 'starred-projects'
  | 'team-readme-section'
  | 'team_usage_chart'
  | 'unlimited_tracked_hours_for_all'
  | 'uplot_user'
  | 'use-backend-smoothing-entity'
  | 'use-backend-smoothing-user'
  | 'weave_debounced_scoring'
  | 'eval_tables'
  | 'weave_panels_in_workspace'
  | 'weave_remote_scoring'
  | 'wandb-slurm-daemon'
  | 'enable_wb_agent_usage_charts'
  | 'weave_trace_analysis_frontend'
  | 'workspace_agent'
  | 'workspace_feedback_org'
  | 'workspace_column_editor_redesign_org'
  | 'workspace_column_editor_redesign_user'
  | 'workspace_filter_visibility_ui'
  | 'runs_table_count_label_wording'
  | 'runs_table_results_hidden_by_filters_banner'
  | 'video_streaming_entity'
  | 'enable_account_homepage'
  | 'automations_free'
  | 'clientside_dynamic_timeouts_entity'
  | 'enable_homepage_refresh';

function getFeatureFlagValue(
  featureFlags: FeatureFlagList | null | undefined,
  rampKey: RampKey | string
): boolean | undefined {
  return (
    featureFlags?.find(flag => flag?.rampKey === rampKey)?.isEnabled ??
    undefined
  );
}

export const useEntityRampFlagWithLoading = (
  entityName: string | undefined,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getEntityFlags: typeof useEntityFeatureFlagsQuery = useEntityFeatureFlagsQuery
) => {
  const {data, loading} = getEntityFlags({
    variables: {teamName: entityName, rampIDType},
    skip: entityName == null,
  });

  const featureFlags = data?.entity?.featureFlags;
  let flagValue: boolean;
  let flagLoading: boolean;
  let datadogFlagValue: boolean;
  // All features are enabled for CI, unless explicitly disabled in their own tests.
  if (isIntegrationBypassActive(rampKey)) {
    flagValue = true;
    flagLoading = false;
    datadogFlagValue = true;
  } else if (!featureFlags) {
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useEntityRampFlag: returning false due to no data');
    flagValue = false;
    flagLoading = loading;
    datadogFlagValue = false;
  } else {
    const isEnabled = getFeatureFlagValue(featureFlags, rampKey) ?? false;
    flagValue = isEnabled;
    flagLoading = loading;
    datadogFlagValue = isEnabled;
  }

  // Log in an effect, not in render, so the render path stays side-effect free.
  useDatadogFeatureFlagEvaluation(rampKey, datadogFlagValue);
  return {data: flagValue, loading: flagLoading};
};

export const useEntityRampFlag = (
  entityName: string | undefined,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getEntityFlags: typeof useEntityFeatureFlagsQuery = useEntityFeatureFlagsQuery
) => {
  return useEntityRampFlagWithLoading(
    entityName,
    rampKey,
    rampIDType,
    getEntityFlags
  ).data;
};
export const checkEntityRampFlag = async (
  entityName: string,
  rampKey: RampKey
) => {
  const {data, errors} = await apolloClient.query<
    EntityFeatureFlagsQuery,
    EntityFeatureFlagsQueryVariables
  >({
    query: EntityFeatureFlagsDocument,
    // Imperative (non-React) call: bypass the Apollo cache so sagas always get
    // a fresh value and don't interact with the React hook's cache entry.
    fetchPolicy: 'no-cache',
    variables: {
      teamName: entityName,
      rampIDType: RampIdType.EntityName,
    },
  });
  const teamFlagsGlobalList = data?.entity?.featureFlags;
  if (!teamFlagsGlobalList || errors) {
    return false;
  }

  const isEnabled =
    teamFlagsGlobalList.find(f => f?.rampKey === rampKey)?.isEnabled ?? false;

  // This isn't ideal, but this fn is called outside the context of a React
  // hook, so the hook-based flag-evaluation helpers aren't available to us.
  getDatadogRum().addFeatureFlagEvaluation(rampKey, isEnabled);

  return isEnabled;
};

/**
 * Check a viewer-level ramp flag outside of React hooks.
 * This is useful in contexts like Redux sagas where hooks cannot be used.
 * It makes a direct GraphQL query to fetch the viewer's feature flags.
 */
export const checkViewerRampFlag = async (
  client: ApolloClient,
  rampKey: RampKey
): Promise<boolean> => {
  try {
    const {data, errors} = await client.query<
      ViewerFeatureFlagsQuery,
      ViewerFeatureFlagsQueryVariables
    >({
      query: ViewerFeatureFlagsDocument,
      // Imperative (non-React) call: bypass the Apollo cache so sagas always
      // get a fresh value and don't interact with the React hook's cache entry.
      fetchPolicy: 'no-cache',
      variables: {
        rampIDType: RampIdType.UserId,
      },
    });

    if (!data?.viewer?.featureFlags || errors) {
      return false;
    }

    const isEnabled =
      data.viewer.featureFlags.find(f => f?.rampKey === rampKey)?.isEnabled ??
      false;

    getDatadogRum().addFeatureFlagEvaluation(rampKey, isEnabled);

    return isEnabled;
  } catch (err) {
    console.error('Failed to check viewer ramp flag:', err);
    return false;
  }
};

/**
 * Warm the Apollo cache for the viewer feature-flags query (as read by
 * `useUserRampFlag*` with `RampIdType.UserName`) before the app renders.
 *
 * Flag consumers that mount alongside their own data queries (e.g.
 * TeamProjectTable) otherwise race those queries for first response, and can
 * act on a default `false` that flips once the flags arrive. Until React 19
 * prep removed it, `ConcurrentRenderModeSwitcher` issued this query at boot as
 * a side effect and hid that race.
 */
export function prefetchViewerRampFlags(): void {
  apolloClient
    .query<ViewerFeatureFlagsQuery, ViewerFeatureFlagsQueryVariables>({
      query: ViewerFeatureFlagsDocument,
      variables: {rampIDType: RampIdType.UserName},
    })
    .catch(() => {
      // Best-effort warm-up: on failure the flag hooks fetch on demand.
    });
}

// There is a global store using zustand that stores the return value of this query call to useViewerFeatureFlagsQuery

export const useUserRampFlagWithLoading = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getUserFlags: typeof useViewerFeatureFlagsQuery = useViewerFeatureFlagsQuery
) => {
  const {data, loading} = getUserFlags({variables: {rampIDType}});

  const featureFlags = data?.viewer?.featureFlags;
  // note: don't default to false! undefined means not set, which is also valuable info
  // `isEnabled` stays `undefined` when the key isn't in the list so
  // `useUserOrOrgRampFlag` can fall back to orgs.
  let isEnabled: boolean | undefined;
  if (!featureFlags) {
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useUserRampFlag: returning false due to no data');
    isEnabled = false;
  } else {
    isEnabled = getFeatureFlagValue(featureFlags, rampKey);
  }

  useDatadogFeatureFlagEvaluation(rampKey, isEnabled);

  // `hasData` lets fail-open callers distinguish an explicit `false` from a
  // failed/empty flags response (where `isEnabled` is also `false`).
  return {isEnabled, loading, hasData: featureFlags != null};
};

export const useUserRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getUserFlags: typeof useViewerFeatureFlagsQuery = useViewerFeatureFlagsQuery
) => {
  return useUserRampFlagWithLoading(rampKey, rampIDType, getUserFlags)
    .isEnabled;
};

export const useOrgRampFlagWithLoading = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  orgId: string | null = null,
  useGetOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const {data, loading} = useGetOrgFlags({
    variables: {rampIDType},
  });

  const orgs = data?.viewer?.organizations;
  // When `orgId` is set, only that org is checked; otherwise returns `true`
  // if any org has the flag enabled.
  let result: {data: boolean | undefined; loading: boolean};
  if (loading || !orgs) {
    result = {data: undefined, loading};
  } else if (orgId != null && orgId.trim() !== '') {
    // If orgId is provided and not empty, only check that specific org
    const specificOrg = orgs.find(org => org.id === orgId);
    if (!specificOrg) {
      result = {data: false, loading: false};
    } else {
      result = {
        data: getFeatureFlagValue(specificOrg.featureFlags, rampKey),
        loading: false,
      };
    }
  } else {
    // Otherwise check all orgs (existing behavior)
    let isOrgFlagEnabled: boolean | undefined; // undefined means not set
    let hasAnyEnabledFlag = false;
    for (const org of orgs) {
      isOrgFlagEnabled = getFeatureFlagValue(org.featureFlags, rampKey);
      if (isOrgFlagEnabled) {
        hasAnyEnabledFlag = true;
        break;
      }
    }
    result = {
      data: hasAnyEnabledFlag ? true : isOrgFlagEnabled,
      loading: false,
    };
  }

  useDatadogFeatureFlagEvaluation(rampKey, result.data);
  return result;
};

// This fetches all the orgs the user is a member of and returns true for a ramp flag if any
// of the orgs has the ramp flag enabled
export const useOrgRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const ret = useOrgRampFlagWithLoading(rampKey, rampIDType, null, getOrgFlags);
  return !ret.loading && ret.data;
};

// This uses the orgID (string encoded) to find the if the org's feature flag is enabled
export const useOrgIdRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  orgId: string | null = null,
  getOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const ret = useOrgRampFlagWithLoading(
    rampKey,
    rampIDType,
    orgId,
    getOrgFlags
  );
  return !ret.loading && ret.data;
};

export const useOrgNameRampFlag = (
  orgName: string,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useOrganizationFeatureFlagsQuery = useOrganizationFeatureFlagsQuery
) => {
  const {data, loading} = getOrgFlags({
    variables: {orgName, rampIDType},
    skip: orgName == null,
    context: propagateErrorsContext(),
  });

  const orgFeatureFlags = data?.organization?.featureFlags;

  let result;
  let datadogFlagValue;

  if (loading || orgFeatureFlags == null) {
    datadogFlagValue = false;
    result = {data: false, loading};
  } else if (data?.organization?.orgType === OrgType.Personal) {
    datadogFlagValue = false;
    result = {data: false, loading};
  } else {
    const isEnabled = getFeatureFlagValue(orgFeatureFlags, rampKey) ?? false;
    datadogFlagValue = isEnabled;
    result = {
      data: isEnabled,
      loading,
    };
  }

  // Keep evaluation logging centralized in an effect-driven hook to prevent
  // per-render Datadog calls in frequently rendered components.
  // Docs:
  // - https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/setup/?tab=browser
  // - https://react.dev/learn/synchronizing-with-effects
  useDatadogFeatureFlagEvaluation(rampKey, datadogFlagValue);
  return result;
};

/**
 * Checks viewer's user-level and org-level ramp flags for the given keys.
 * Note: currently there's no way for a user flag to take precendence over
 * an org flag, because the backend system will return a boolean for all
 * ramp keys, even ramp keys for which there is no entry that applies to
 * the user.
 */
export const useUserOrOrgRampFlag = (
  userRampKey: RampKey,
  orgRampKey: RampKey
) => {
  const userFlag = useUserRampFlag(userRampKey, RampIdType.UserName);
  const orgFlag = useOrgRampFlag(orgRampKey, RampIdType.OrgName);
  return userFlag || orgFlag || false;
};

export const useHideSummaryHistogramsRampFlag = (
  entityName: string | undefined
) => {
  return useEntityRampFlagWithLoading(
    entityName,
    'hide-summary-histograms',
    RampIdType.EntityName
  );
};
